import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import SmoothScroll from "@/components/SmoothScroll";
import SEO from "@/components/SEO";
import api, { absUrl } from "@/lib/api";

const TOURS_HERO =
  "https://images.pexels.com/photos/35428411/pexels-photo-35428411.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=2400";

export default function Tours() {
  const { t } = useTranslation();
  const FILTERS = [
    { key: "All", label: t("tours.filter_all") },
    { key: "Bespoke", label: t("tours.filter_bespoke") },
    { key: "Adventure", label: t("tours.filter_adventure") },
    { key: "Cultural", label: t("tours.filter_cultural") },
  ];
  const [tours, setTours] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(
          "/tours",
          filter !== "All" ? { params: { category: filter } } : {}
        );
        setTours(r.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [filter]);

  return (
    <SmoothScroll>
      <div className="min-h-screen bg-[#FDFBF7]">
        <SEO
          title="Tours — Bespoke private journeys across Indonesia"
          description="Hand-curated private tours of Bali, Komodo, Raja Ampat, Yogyakarta and beyond. Bespoke, adventure and cultural itineraries for international travelers."
          image={TOURS_HERO}
        />
        <Navbar transparent />

        <PageHero
          image={TOURS_HERO}
          eyebrow={t("tours.eyebrow")}
          title={t("tours.title_a")}
          titleBold={t("tours.title_b")}
          subtitle="Hand-curated private itineraries — bespoke, adventure, or cultural — designed by a local team and led by guides we have known for years."
        />

        <main
          className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24"
          data-testid="tours-page"
        >
          {/* Filter row */}
          <div className="flex flex-wrap gap-2" data-testid="tour-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key.toLowerCase()}`}
                onClick={() => setFilter(f.key)}
                className={`px-5 py-2 rounded-full text-sm transition-colors ${
                  filter === f.key
                    ? "bg-slate-900 text-white shadow-md"
                    : "bg-white text-slate-700 border border-slate-200 hover:border-slate-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="mt-16 text-slate-400">{t("tours.loading")}</div>
          ) : tours.length === 0 ? (
            <div className="mt-16 text-slate-500">{t("tours.no_results")}</div>
          ) : (
            <div
              className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              data-testid="tour-grid"
            >
              {tours.map((tt, i) => (
                <Link
                  key={tt.tour_id}
                  to={`/tours/${tt.slug}`}
                  data-testid={`tour-card-${tt.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <div
                    className={`aspect-[4/3] overflow-hidden bg-slate-100 relative ${
                      i % 2 === 0 ? "paper-tear-card" : "paper-tear-strip"
                    } paper-grain`}
                  >
                    <img
                      src={absUrl(tt.cover_image_url)}
                      alt={tt.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div className="t-overline text-seafoam mb-2">
                      {tt.category} · {tt.destination}
                    </div>
                    <h3 className="text-xl font-medium text-slate-900 leading-snug">
                      <b>{tt.title}</b>
                    </h3>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-sm text-slate-500">{tt.duration}</span>
                      <span className="text-base font-medium text-slate-900">
                        ${tt.price_usd.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </main>
        <Footer />
      </div>
    </SmoothScroll>
  );
}
